import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import PerText from '../Text'
import PerButton from '../Button'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { useTargetTemplateModalStore } from './TargetTemplateModalStore'
import { NoteInfoInputExtended } from './types'
import { nanoid } from 'nanoid'
import { transformTargetTemplateBeforeSave } from './util'
import { targetTemplateModalSaveTarget } from './async'
import { useCompanySettingsStore } from '../../../modules/companySettings/CompanySettingsStore'
import { ObjectiveTemplateInput } from '../../../types'
import { showNotification } from '../Notification/notification'

export const TargetTemplateModalFooter: FC = () => {
  const [selected] = useCompanySettingsStore((state) => [state.selected], shallow)
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateToUtcString } = useDateTime(user)
  const [isProcessing, toggleProcessing] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [closeTargetTemplateModal, cleanTargetTemplateModal, newTargetTemplateForm, currentObjective, currentComments, commentFromInput] =
    useTargetTemplateModalStore(
      (state) => [
        state.closeTargetTemplateModal,
        state.cleanTargetTemplateModal,
        state.newTargetTemplateForm,
        state.currentObjective,
        state.currentComments,
        state.commentFromInput,
      ],
      shallow,
    )

  const handleClose = () => {
    closeTargetTemplateModal()
    setTimeout(() => {
      cleanTargetTemplateModal()
    }, 300)
  }

  const saveTarget = async () => {
    if (!!newTargetTemplateForm) {
      const _commentFromInput = !!commentFromInput
        ? ({
            id: nanoid(),
            text: commentFromInput.trim(),
            date: formatDateToUtcString(new Date()),
            isNewComment: false,
            author: { id: user.employeeId, firstName: user.firstName, lastName: user.lastName, avatarUrl: user.avatarUrl },
          } as NoteInfoInputExtended)
        : undefined

      const formValues = newTargetTemplateForm.getValues() as ObjectiveTemplateInput

      const objective: ObjectiveTemplateInput = transformTargetTemplateBeforeSave(user, formValues, currentComments, _commentFromInput)

      const { errors } = await targetTemplateModalSaveTarget(selected.companyId, selected.country, objective)
      if (!!errors) {
        setErrorMessage(t('targetTemplateModal.targetError'))
        toggleProcessing(false)
      } else {
        const operationType = !!currentObjective && !!currentObjective.id ? 'update' : 'create'
        showNotification({
          type: 'success',
          message: t(`targetTemplateModal.notifications.${operationType}.success.title`),
          description: t(`targetTemplateModal.notifications.${operationType}.success.description`),
        })
        handleClose()
      }
    }
  }

  const handleSubmit = async () => {
    if (!!newTargetTemplateForm && !!newTargetTemplateForm.trigger) {
      await newTargetTemplateForm.trigger()
      if (Object.keys(newTargetTemplateForm.formState.errors).length) {
        setErrorMessage(t('targetTemplateModal.formErrors'))
      } else {
        toggleProcessing(true)
        await saveTarget()
      }
    }
  }

  return (
    <PerFlexRow align="middle" justify="space-between">
      <PerFlexRow>
        <PerButton
          text={t('global.button.cancel')}
          color="secondary"
          before="close_stroke"
          variant="pastel"
          type="button"
          onClick={handleClose}
        />
      </PerFlexRow>
      <PerFlexRow align="middle">
        {!!errorMessage && (
          <PerText appearance="info" color="error">
            {errorMessage}
          </PerText>
        )}
        <PerSpacer ml="xxxs" mr="xxxs" />
        <PerButton
          text={t('global.button.save')}
          color="success"
          before="check_stroke"
          variant="pastel"
          type="button"
          isProcessing={{ active: isProcessing }}
          onClick={handleSubmit}
          data-e2e-targets-modal-button-confirm
        />
      </PerFlexRow>
    </PerFlexRow>
  )
}
